/**
 * Scope-grouped table for the operating-config surfaces.
 *
 * Renders an "Organization default" row + one row per site, each expandable to
 * its own editable detail (mounted lazily on expand). Mirrors the Reunification
 * `PlansList` pattern: a `DataTable` with an expand column + `renderExpandedRow`.
 * The org default and each site are edited independently; inheritance shows via
 * a status badge.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, HStack, Spinner, Text } from '@chakra-ui/react';
import {
  DataTable,
  StatusBadge,
  TableOptions,
  type Column,
  type StatusType,
} from '@themes/aegix';
import {
  HiBuildingOffice2,
  HiChevronDown,
  HiChevronRight,
  HiMapPin,
} from 'react-icons/hi2';
import type { ScopeRow } from './types';

export interface ScopeGroupTableProps {
  rows: ScopeRow[];
  isLoading: boolean;
  error?: unknown;
  /** Optional column rendered after Status (e.g. a per-scope summary). */
  summaryColumn?: Column<ScopeRow>;
  /** Override the default Inherited/Override status badge. */
  statusFor?: (row: ScopeRow) => { status: StatusType; label: string };
  /** Detail shown when a row is expanded. Mounted only while expanded. */
  renderExpandedRow: (row: ScopeRow) => React.ReactNode;
  /** When set, shows a search box that filters rows by name. */
  searchPlaceholder?: string;
  emptyText?: string;
  e2eTitle?: string;
}

function defaultStatus(row: ScopeRow): { status: StatusType; label: string } {
  if (row.hasOwn) {
    return row.kind === 'org'
      ? { status: 'active' as StatusType, label: 'Configured' }
      : { status: 'active' as StatusType, label: 'Override' };
  }
  return row.kind === 'org'
    ? { status: 'pending' as StatusType, label: 'Not set' }
    : { status: 'info' as StatusType, label: 'Inherited' };
}

export function ScopeGroupTable({
  rows,
  isLoading,
  error,
  summaryColumn,
  statusFor,
  renderExpandedRow,
  searchPlaceholder,
  emptyText,
  e2eTitle = 'ScopeGroupTable',
}: ScopeGroupTableProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const defaultStatusLocal = (row: ScopeRow): { status: StatusType; label: string } => {
    if (row.hasOwn) {
      return row.kind === 'org'
        ? { status: 'active' as StatusType, label: t('whakahaere.statusConfigured', 'Configured') }
        : { status: 'active' as StatusType, label: t('whakahaere.statusOverride', 'Override') };
    }
    return row.kind === 'org'
      ? { status: 'pending' as StatusType, label: t('whakahaere.statusNotSet', 'Not set') }
      : { status: 'info' as StatusType, label: t('whakahaere.statusInherited', 'Inherited') };
  };

  const getStatus = statusFor ?? defaultStatusLocal;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const label =
        r.kind === 'org'
          ? `organization default ${r.districtName}`
          : (r.schoolName ?? '');
      return label.toLowerCase().includes(q);
    });
  }, [rows, search]);

  const columns: Column<ScopeRow>[] = useMemo(() => {
    const base: Column<ScopeRow>[] = [
      {
        key: 'expand',
        label: '',
        sortable: false,
        width: '40px',
        render: (row) => (
          <Box color="text.secondary" p={1}>
            {expanded.has(row.key) ? (
              <HiChevronDown size={16} />
            ) : (
              <HiChevronRight size={16} />
            )}
          </Box>
        ),
      },
      {
        key: 'name',
        label: t('whakahaere.scope', 'Scope'),
        sortable: false,
        render: (row) => (
          <HStack gap={2}>
            <Box color="text.secondary" flexShrink={0}>
              {row.kind === 'org' ? (
                <HiBuildingOffice2 size={15} />
              ) : (
                <HiMapPin size={15} />
              )}
            </Box>
            <Text fontWeight={row.kind === 'org' ? 600 : 500} fontSize="sm">
              {row.kind === 'org' ? t('whakahaere.orgDefault', 'Organization default') : row.schoolName}
            </Text>
            {row.kind === 'org' && (
              <Text fontSize="xs" color="text.secondary">
                {t('whakahaere.inheritedByAllSites', '(inherited by all sites)')}
              </Text>
            )}
          </HStack>
        ),
      },
      {
        key: 'status',
        label: t('whakahaere.status', 'Status'),
        sortable: false,
        width: '140px',
        render: (row) => {
          const s = getStatus(row);
          return <StatusBadge status={s.status} label={s.label} />;
        },
      },
    ];
    return summaryColumn ? [...base, summaryColumn] : base;
  }, [expanded, getStatus, summaryColumn, t]);

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <Text color="text.danger">
          {error instanceof Error ? error.message : t('whakahaere.failedToLoad', 'Failed to load')}
        </Text>
      </Center>
    );
  }

  return (
    <>
      {searchPlaceholder && (
        <TableOptions
          searchPlaceholder={searchPlaceholder ?? t('whakahaere.searchSites', 'Search sites')}
          searchValue={search}
          onSearchChange={setSearch}
          showFilter={false}
        />
      )}
      <Box px={5} pb={5}>
        {visibleRows.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">{emptyText ?? t('whakahaere.noSitesFound', 'No sites found')}</Text>
          </Center>
        ) : (
          <DataTable
            columns={columns}
            data={visibleRows}
            keyExtractor={(row) => row.key}
            onRowClick={(row) => toggle(row.key)}
            renderExpandedRow={(row) =>
              expanded.has(row.key) ? (
                <Box
                  ml={6}
                  pl={4}
                  py={2}
                  borderLeftWidth={2}
                  borderColor="border.primary"
                  data-e2e={`${e2eTitle}-expanded-${row.key}`}
                >
                  {renderExpandedRow(row)}
                </Box>
              ) : null
            }
          />
        )}
      </Box>
    </>
  );
}
